import React, { useState, useEffect } from 'react';
import { apiClient } from '../App';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Package, Users, ShoppingBag, PencilSimple, Trash, Plus, Tag } from '@phosphor-icons/react';

const AdminDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '', description: '', base_price: '', image_url: '',
    category: 'corporate', features: '', sizes: '500ml, 1L, 2L', stock: 100
  });
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: '', discount_percent: '', max_uses: 100 });
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => { fetchAdminData(); }, []);

  const fetchAdminData = async () => {
    try {
      const [ordersRes, usersRes, productsRes, couponsRes, notifsRes] = await Promise.all([
        apiClient.get('/admin/orders'),
        apiClient.get('/admin/users'),
        apiClient.get('/products'),
        apiClient.get('/coupons').catch(() => ({ data: { coupons: [] } })),
        apiClient.get('/admin/notifications').catch(() => ({ data: { notifications: [] } }))
      ]);
      setOrders(ordersRes.data.orders);
      setUsers(usersRes.data.users);
      setProducts(productsRes.data);
      setCoupons(couponsRes.data.coupons || []);
      setNotifications(notifsRes.data.notifications || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally { setLoading(false); }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await apiClient.put(`/admin/orders/${orderId}/status?status=${newStatus}`);
      toast.success('Order status updated');
      fetchAdminData();
    } catch (error) { toast.error('Failed to update order status'); }
  };

  const openEditProduct = (product) => {
    setEditProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      base_price: product.base_price.toString(),
      image_url: product.image_url,
      category: product.category,
      features: product.features.join(', '),
      sizes: product.sizes.join(', '),
      stock: product.stock
    });
    setShowProductForm(true);
  };

  const openNewProduct = () => {
    setEditProduct(null);
    setProductForm({
      name: '', description: '', base_price: '', image_url: '',
      category: 'corporate', features: '', sizes: '500ml, 1L, 2L', stock: 100
    });
    setShowProductForm(true);
  };

  const saveProduct = async () => {
    const data = {
      name: productForm.name,
      description: productForm.description,
      base_price: parseFloat(productForm.base_price),
      image_url: productForm.image_url,
      category: productForm.category,
      features: productForm.features.split(',').map(f => f.trim()).filter(f => f),
      sizes: productForm.sizes.split(',').map(s => s.trim()).filter(s => s),
      stock: parseInt(productForm.stock) || 100
    };

    try {
      if (editProduct) {
        await apiClient.put(`/products/${editProduct.id}`, data);
        toast.success('Product updated!');
      } else {
        await apiClient.post('/products', data);
        toast.success('Product created!');
      }
      setShowProductForm(false);
      fetchAdminData();
    } catch (error) { toast.error('Failed to save product'); }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await apiClient.delete(`/products/${productId}`);
      toast.success('Product deleted');
      fetchAdminData();
    } catch (error) { toast.error('Failed to delete product'); }
  };

  const createCoupon = async () => {
    try {
      await apiClient.post('/coupons', {
        code: couponForm.code, discount_percent: parseFloat(couponForm.discount_percent),
        max_uses: parseInt(couponForm.max_uses) || 100
      });
      toast.success('Coupon created!');
      setShowCouponForm(false);
      setCouponForm({ code: '', discount_percent: '', max_uses: 100 });
      fetchAdminData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create coupon'); }
  };

  const deleteCoupon = async (couponId) => {
    try {
      await apiClient.delete(`/coupons/${couponId}`);
      toast.success('Coupon deleted');
      fetchAdminData();
    } catch (error) { toast.error('Failed to delete coupon'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-slate-400">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-8 md:px-16 lg:px-24">
        <h1 className="text-4xl sm:text-5xl font-medium mb-8 text-foreground" data-testid="admin-heading">
          Admin Dashboard
        </h1>

        {/* Order Notifications with WhatsApp */}
        {notifications.filter(n => !n.read).length > 0 && (
          <div className="mb-8 bg-card border border-primary/30 p-6" data-testid="notifications-section">
            <h2 className="text-lg font-heading text-foreground mb-4 flex items-center gap-2">
              <span className="bg-[#D4AF37] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">{notifications.filter(n => !n.read).length}</span>
              New Order Notifications
            </h2>
            <div className="space-y-3">
              {notifications.filter(n => !n.read).slice(0, 5).map((notif, index) => (
                <div key={notif.id} className="flex items-center justify-between bg-background p-4 border border-border" data-testid={`notification-${index}`}>
                  <div>
                    <p className="text-sm text-foreground">New order #{(notif.order_id || '').slice(0, 8)}</p>
                    <p className="text-xs text-slate-400">{new Date(notif.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-2">
                    <a href={notif.whatsapp_link} target="_blank" rel="noopener noreferrer"
                      className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-2 rounded-sm flex items-center gap-1"
                      data-testid={`wa-notify-${index}`}>
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M12 0C5.373 0 0 5.373 0 12c0 2.121.553 4.113 1.519 5.844L.053 23.577l5.933-1.556A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
                      View on WhatsApp
                    </a>
                    <Button variant="ghost" size="sm" onClick={async () => {
                      await apiClient.put(`/admin/notifications/${notif.id}/read`);
                      fetchAdminData();
                    }} data-testid={`dismiss-notif-${index}`}>
                      Dismiss
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-card border border-border p-6" data-testid="stats-orders">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 mb-1">Total Orders</p>
                <p className="text-3xl font-medium text-[#D4AF37]">{orders.length}</p>
              </div>
              <Package size={48} weight="thin" className="text-slate-600" />
            </div>
          </div>
          <div className="bg-card border border-border p-6" data-testid="stats-users">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 mb-1">Total Users</p>
                <p className="text-3xl font-medium text-[#D4AF37]">{users.length}</p>
              </div>
              <Users size={48} weight="thin" className="text-slate-600" />
            </div>
          </div>
          <div className="bg-card border border-border p-6" data-testid="stats-products">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 mb-1">Total Products</p>
                <p className="text-3xl font-medium text-[#D4AF37]">{products.length}</p>
              </div>
              <ShoppingBag size={48} weight="thin" className="text-slate-600" />
            </div>
          </div>
        </div>

        <Tabs defaultValue="products" className="w-full" data-testid="admin-tabs">
          <TabsList className="grid w-full max-w-lg grid-cols-4">
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          </TabsList>

          {/* PRODUCTS TAB with EDIT functionality */}
          <TabsContent value="products" className="mt-6" data-testid="products-tab-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-heading text-foreground">Manage Products</h2>
              <Button onClick={openNewProduct} className="rounded-sm" data-testid="add-product-button">
                <Plus size={18} className="mr-2" /> Add Product
              </Button>
            </div>

            {/* Product Edit Dialog */}
            <Dialog open={showProductForm} onOpenChange={setShowProductForm}>
              <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="product-form-desc" data-testid="product-form-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editProduct ? 'Edit Product' : 'Add New Product'}
                  </DialogTitle>
                  <p id="product-form-desc" className="text-sm text-slate-400">Fill in the product details below</p>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Product Name</Label>
                    <Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} placeholder="e.g., Corporate PET Bottle" data-testid="product-form-name" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <textarea value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} placeholder="Product description..." rows={3} className="flex w-full rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" data-testid="product-form-description" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Price ($)</Label>
                      <Input type="number" step="0.01" value={productForm.base_price} onChange={(e) => setProductForm({...productForm, base_price: e.target.value})} placeholder="15.00" data-testid="product-form-price" />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={productForm.category} onValueChange={(v) => setProductForm({...productForm, category: v})}>
                        <SelectTrigger data-testid="product-form-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="corporate">Corporate</SelectItem>
                          <SelectItem value="wedding">Wedding</SelectItem>
                          <SelectItem value="personal">Personal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Image URL</Label>
                    <Input value={productForm.image_url} onChange={(e) => setProductForm({...productForm, image_url: e.target.value})} placeholder="https://..." data-testid="product-form-image" />
                    {productForm.image_url && (
                      <img src={productForm.image_url} alt="Preview" className="mt-2 h-32 object-cover border border-border" />
                    )}
                  </div>
                  <div>
                    <Label>Features (comma separated)</Label>
                    <Input value={productForm.features} onChange={(e) => setProductForm({...productForm, features: e.target.value})} placeholder="Feature 1, Feature 2, Feature 3" data-testid="product-form-features" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Sizes (comma separated)</Label>
                      <Input value={productForm.sizes} onChange={(e) => setProductForm({...productForm, sizes: e.target.value})} placeholder="500ml, 1L, 2L" data-testid="product-form-sizes" />
                    </div>
                    <div>
                      <Label>Stock</Label>
                      <Input type="number" value={productForm.stock} onChange={(e) => setProductForm({...productForm, stock: e.target.value})} data-testid="product-form-stock" />
                    </div>
                  </div>
                  <Button onClick={saveProduct} className="w-full rounded-sm" data-testid="product-form-save">
                    {editProduct ? 'Update Product' : 'Create Product'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product, index) => (
                <div key={product.id} className="bg-card border border-border overflow-hidden" data-testid={`admin-product-card-${index}`}>
                  <img src={product.image_url} alt={product.name} className="w-full h-48 object-cover" />
                  <div className="p-6">
                    <h3 className="text-lg font-normal mb-2 text-foreground" data-testid={`admin-product-name-${index}`}>
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-400 mb-2">{product.category}</p>
                    <p className="text-xl font-medium text-[#D4AF37] mb-2">${product.base_price.toFixed(2)}</p>
                    <p className="text-xs text-slate-500 mb-4">Sizes: {product.sizes.join(', ')} | Stock: {product.stock}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1 rounded-sm" onClick={() => openEditProduct(product)} data-testid={`edit-product-${index}`}>
                        <PencilSimple size={16} className="mr-1" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm text-red-400 hover:text-red-300" onClick={() => deleteProduct(product.id)} data-testid={`delete-product-${index}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ORDERS TAB */}
          <TabsContent value="orders" className="mt-6" data-testid="orders-tab-content">
            <div className="bg-card border border-border">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Order ID</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Customer</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Total</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Payment</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-slate-400">No orders yet</td></tr>
                    )}
                    {orders.map((order, index) => (
                      <tr key={order.id || index} className="border-b border-border" data-testid={`admin-order-row-${index}`}>
                        <td className="p-4 text-sm text-slate-300">#{(order.id || '').slice(0, 8)}</td>
                        <td className="p-4 text-sm text-slate-300">{order.user_email || 'N/A'}</td>
                        <td className="p-4 text-sm text-[#D4AF37]">${(order.final_amount || order.total_amount || 0).toFixed(2)}</td>
                        <td className="p-4 text-sm text-slate-300">
                          <span className="block">{order.payment_method === 'cod' ? 'COD' : 'Online'}</span>
                          <span className="text-xs text-slate-500">{order.payment_status || 'N/A'}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-xs uppercase px-2 py-1 rounded bg-[#D4AF37]/20 text-[#D4AF37]">{order.status}</span>
                        </td>
                        <td className="p-4">
                          <Select value={order.status} onValueChange={(s) => updateOrderStatus(order.id, s)}>
                            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="processing">Processing</SelectItem>
                              <SelectItem value="shipped">Shipped</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* COUPONS TAB */}
          <TabsContent value="coupons" className="mt-6" data-testid="coupons-tab-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-heading text-foreground">Manage Coupons & Offers</h2>
              <Dialog open={showCouponForm} onOpenChange={setShowCouponForm}>
                <DialogTrigger asChild>
                  <Button className="rounded-sm" data-testid="add-coupon-button"><Plus size={18} className="mr-2" /> Create Coupon</Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border" aria-describedby="coupon-form-desc" data-testid="coupon-form-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Create Discount Coupon</DialogTitle>
                    <p id="coupon-form-desc" className="text-sm text-slate-400">Create a coupon code customers can use at checkout</p>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div><Label>Coupon Code</Label><Input value={couponForm.code} onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})} placeholder="e.g., WELCOME10" data-testid="coupon-code-input" /></div>
                    <div><Label>Discount (%)</Label><Input type="number" value={couponForm.discount_percent} onChange={(e) => setCouponForm({...couponForm, discount_percent: e.target.value})} placeholder="e.g., 10" data-testid="coupon-discount-input" /></div>
                    <div><Label>Max Uses</Label><Input type="number" value={couponForm.max_uses} onChange={(e) => setCouponForm({...couponForm, max_uses: e.target.value})} placeholder="100" data-testid="coupon-max-uses-input" /></div>
                    <Button onClick={createCoupon} className="w-full rounded-sm" data-testid="save-coupon-btn">Create Coupon</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {coupons.length === 0 ? (
                <div className="col-span-3 bg-card border border-border p-8 text-center"><p className="text-slate-400">No coupons yet. Create one to offer discounts!</p></div>
              ) : coupons.map((coupon, index) => (
                <div key={coupon.id} className="bg-card border border-border p-6" data-testid={`coupon-card-${index}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Tag size={20} className="text-[#D4AF37]" />
                      <span className="text-lg font-medium text-[#D4AF37]">{coupon.code}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteCoupon(coupon.id)} data-testid={`delete-coupon-${index}`}>
                      <Trash size={16} className="text-red-400" />
                    </Button>
                  </div>
                  <p className="text-2xl font-medium text-foreground mb-1">{coupon.discount_percent}% OFF</p>
                  <p className="text-xs text-slate-400">Used: {coupon.used_count || 0} / {coupon.max_uses}</p>
                  <p className="text-xs text-slate-500 mt-1">{coupon.is_active ? 'Active' : 'Inactive'}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users" className="mt-6" data-testid="users-tab-content">
            <div className="bg-card border border-border">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Name</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Email</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Role</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.id} className="border-b border-border" data-testid={`admin-user-row-${index}`}>
                        <td className="p-4 text-sm text-slate-300">{user.name}</td>
                        <td className="p-4 text-sm text-slate-300">{user.email}</td>
                        <td className="p-4">
                          <span className={`text-xs uppercase px-2 py-1 rounded ${user.role === 'admin' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-slate-700 text-slate-300'}`}>{user.role}</span>
                        </td>
                        <td className="p-4 text-sm text-slate-400">{new Date(user.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
